import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DemoOrder {
  id: string;
  symbol: string;
  side: string;
  quantity: number;
  price: number | null;
  status: string;
  order_type: string;
  created_at: string;
  strategy_instance_id: string;
}

export function useDemoAccountOrders(accountId: string) {
  const { user } = useAuth();
  const [orders, setOrders] = useState<DemoOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    if (!user?.id || !accountId) {
      setOrders([]);
      setLoading(false);
      return;
    }

    // Find strategy_instance_ids linked to this demo account
    const { data: params } = await supabase
      .from('strategy_parameters')
      .select('strategy_instance_id')
      .eq('param_name', 'demo_account_id')
      .eq('param_value', accountId);

    const instanceIds = params?.map((p) => p.strategy_instance_id) ?? [];

    if (instanceIds.length === 0) {
      setOrders([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('strategy_orders')
      .select('*')
      .eq('user_id', user.id)
      .in('strategy_instance_id', instanceIds)
      .order('created_at', { ascending: false })
      .limit(20);

    setOrders(
      (data ?? []).map((o) => ({
        ...o,
        quantity: Number(o.quantity),
        price: o.price ? Number(o.price) : null,
      }))
    );
    setLoading(false);
  }, [user?.id, accountId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`orders-${accountId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'strategy_orders' }, () => fetchOrders())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, accountId, fetchOrders]);

  return { orders, loading, refetch: fetchOrders };
}
